"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";

import type { Messages } from "./catalog";
import { intlLocaleFor, uiLocaleFor, type ViewerLocale } from "./locales";
import { messages as englishMessages } from "./messages/en";
import { createTranslator, type Translate } from "./translate";

export interface ViewerLanguage {
  language: string | null;
  locale: ViewerLocale;
  intlLocale: string;
  t: Translate;
}

const DEFAULT_INTL_LOCALE = intlLocaleFor(null);

const DEFAULT_VIEWER_LANGUAGE: ViewerLanguage = {
  language: null,
  locale: uiLocaleFor(null),
  intlLocale: DEFAULT_INTL_LOCALE,
  t: createTranslator(DEFAULT_INTL_LOCALE, englishMessages, englishMessages),
};

const ViewerLanguageContext = createContext<ViewerLanguage>(DEFAULT_VIEWER_LANGUAGE);

export function ViewerLanguageProvider({
  language,
  messages,
  children,
}: {
  language: string | null;
  messages: Messages;
  children: ReactNode;
}) {
  const value = useMemo<ViewerLanguage>(() => {
    const intlLocale = intlLocaleFor(language);
    return {
      language,
      locale: uiLocaleFor(language),
      intlLocale,
      t: createTranslator(intlLocale, messages, englishMessages),
    };
  }, [language, messages]);

  return <ViewerLanguageContext.Provider value={value}>{children}</ViewerLanguageContext.Provider>;
}

export function useViewerLanguage(): ViewerLanguage {
  return useContext(ViewerLanguageContext);
}
